import { MenuItem, Select, Stack } from "@mui/material"
import { KeyboardArrowDown } from "@mui/icons-material"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { DeliveryFilterValue, DELIVERY_FILTER_ALL, DeliveryType, GroupBy, getEnumKeyByValue } from "../../meetingServiceTypes"
import { SelectDateRange } from "./SelectDateRange"
import { Dispatch, SetStateAction } from "react"
import { DateFilterParams } from "../../../../../../../shared/components/table/useInitialDateFilters"
import { DeliveryListDateState } from "../OrderDeliveryList"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import GroupBySwitch from "./GroupBySwitch"
import { Box } from "@mui/system"
import HeaderFilter from "./HeaderFilter"

const useStyles = makeStyles()((theme) => ({
    orderSelect: {
        minWidth: "12.5rem",
        padding: 0,
        fontSize: "1rem",
        fontWeight: 500,
        color: "black",
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "& .MuiInputBase-input": {
            padding: 0,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
        "& .MuiSelect-select": {
            paddingRight: "1.5rem !important",
            paddingLeft: 0,
        },
        "& .MuiSelect-icon": {
            display: "none",
        },
    },
    divider: {
        height: "1.25rem",
        width: "1px",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
    value: {
        color: theme.palette.tailwind.slate[900],
        fontWeight: 700,
        marginLeft: "0.5rem",
    },
}))

const SelectTypeComponent = ({ value }: { value: string }) => {
    const { classes } = useStyles()

    return (
        <HeaderFilter
            label={
                <LocalizedStrict
                    id="meeting-service-delivery-list-delivery"
                    vars={{ value }}
                    elems={{
                        value: <span className={classes.value}>{value}</span>,
                    }}
                >
                    <>
                        Delivery type: <span>{value}</span>
                    </>
                </LocalizedStrict>
            }
        >
            <KeyboardArrowDown fontSize="small" />
        </HeaderFilter>
    )
}

type SlipModalHeaderProps = {
    dateState: DeliveryListDateState
    setDateState: (newValue: { startDate: DateTime; endDate: DateTime }) => void
    groupBy: GroupBy
    setGroupBy: (groupBy: GroupBy) => void
    deliveryType: DeliveryFilterValue
    setDeliveryType: Dispatch<SetStateAction<DeliveryFilterValue>>
    isSelectMode: boolean
    showPeriodAll?: boolean
}

export const ModalHeader = ({
    dateState,
    setDateState,
    deliveryType,
    setDeliveryType,
    groupBy,
    setGroupBy,
    isSelectMode,
    showPeriodAll,
}: SlipModalHeaderProps) => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const getDeliveryTypeLabel = (type: DeliveryType) => {
        const enumKey = getEnumKeyByValue(type)
        return l10n.getStringForEnum("delivery-type-filter", enumKey!, enumKey!)
    }

    const getSelectedValueLabel = (value: DeliveryFilterValue): string => {
        if (value === DELIVERY_FILTER_ALL) {
            return l10n.getString("meeting-service-delivery-list-filter-all")
        }
        return getDeliveryTypeLabel(value as DeliveryType)
    }

    return (
        <Stack direction="row" alignItems="center" gap="1rem">
            <GroupBySwitch groupBy={groupBy} setGroupBy={setGroupBy} />
            <Box className={classes.divider} />
            <SelectDateRange
                value={dateState}
                onChange={(newValue) => {
                    setDateState(newValue)
                }}
                paramNameStart={DateFilterParams.START}
                paramNameEnd={DateFilterParams.END}
                showPeriodAll={showPeriodAll}
            />
            <Box className={classes.divider} />
            <Select
                labelId="time-period-select-label"
                id="delivery-type-select"
                value={deliveryType}
                label=""
                className={classes.orderSelect}
                onChange={(e) => {
                    const newValue = e.target.value as DeliveryFilterValue
                    setDeliveryType(newValue)
                }}
                renderValue={(value) => {
                    const displayValue = getSelectedValueLabel(value)
                    return <SelectTypeComponent value={displayValue} />
                }}
                displayEmpty={false}
                variant="outlined"
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                        },
                    },
                }}
                disabled={isSelectMode}
            >
                <MenuItem value={DELIVERY_FILTER_ALL}>
                    <LocalizedStrict id="meeting-service-delivery-list-filter-all">
                        <>All</>
                    </LocalizedStrict>
                </MenuItem>
                {Object.entries(DeliveryType).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                        {getDeliveryTypeLabel(value)}
                    </MenuItem>
                ))}
            </Select>
        </Stack>
    )
}
